import React, { ErrorInfo, ReactNode } from 'react';

import { Sentry } from '../services/sentry';

interface ErrorBoundaryProps {
  children: ReactNode;
  label?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Catches render-time errors anywhere below and shows a banner instead
 * of white-screening the whole tab. Pages with heavy state surface
 * (editor, anything with a reducer) should be wrapped.
 */
export default class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
    this.handleReset = this.handleReset.bind(this);
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, info);
    try {
      Sentry.captureException(error, {
        contexts: { react: { componentStack: info?.componentStack || '' } },
      });
    } catch (_) { /* Sentry not initialized — drop silently */ }
  }

  handleReset(): void {
    this.setState({ error: null });
  }

  override render(): ReactNode {
    const { error } = this.state;
    const { children, label } = this.props;
    if (!error) return children;
    return (
      <div className="error-boundary">
        <div className="banner banner--error">
          <strong>{label || 'Something broke'}</strong>
          <div>{error.message || String(error)}</div>
        </div>
        <button type="button" className="primary" onClick={this.handleReset}>
          Try again
        </button>
        {process.env.NODE_ENV !== 'production' && error.stack && (
          <pre className="error-boundary-stack">{error.stack}</pre>
        )}
      </div>
    );
  }
}
